import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { FileItem } from "@titancloud/shared";
import { getMimeIcon } from "../../utils/mimeIcons";
import { useDownloadUrl } from "../../hooks/useDownloadUrl";
import { useThumbnailUrl } from "../../hooks/useThumbnailUrl";
import { copyImageToClipboard } from "../../utils/copyImage";
import { getDownloadUrls } from "../../api/files.api";
import { getAdminExplorerDownloadUrls } from "../../api/admin.api";
import { getTrashDownloadUrls } from "../../api/trash.api";
import { ImagePreview } from "./ImagePreview";
import { VideoPreview } from "./VideoPreview";
import { AudioPreview } from "./AudioPreview";
import { CodePreview } from "./CodePreview";
import { PdfPreview } from "./PdfPreview";
import { GenericPreview } from "./GenericPreview";
import { PreviewFilmstrip } from "./PreviewFilmstrip";
import { PreviewFileInfo } from "./PreviewFileInfo";


interface PreviewModalProps {
  files: FileItem[];
  initialIndex: number;
  onClose: () => void;
  /** When set, download/thumbnail use admin explorer APIs for this user's files */
  explorerUserId?: string;
  /** When set, download uses trash endpoints (files are soft-deleted but not yet purged) */
  trash?: boolean;
  /** More pages exist beyond `files`; the modal asks for them as the user nears the end. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

function downloadUrlQueryKey(fileId: string, explorerUserId?: string, trash?: boolean) {
  return trash
    ? (["download-url", "trash", fileId] as const)
    : explorerUserId
      ? (["download-url", "admin", explorerUserId, fileId] as const)
      : (["download-url", fileId] as const);
}

// How long to wait for navigation to "settle" before fetching full-res data.
// Short enough that a single deliberate click feels instant, long enough that
// holding the arrow key (browser key-repeat fires every ~30-50ms) coalesces
// into one fetch for wherever the user actually stops, instead of one per
// frame flown past.
const SETTLE_DELAY_MS = 90;

// Request the next page when the user is this many files from the end of the
// loaded list, so it usually lands before they reach the last file.
const LOAD_MORE_THRESHOLD = 5;

export function PreviewModal({
  files,
  initialIndex,
  onClose,
  explorerUserId,
  trash,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: PreviewModalProps) {
  const [index, setIndex] = useState(initialIndex);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [settledIndex, setSettledIndex] = useState(initialIndex);
  const [showInfo, setShowInfo] = useState(false);
  const qc = useQueryClient();

  const file = files[index]!;
  const hasPrev = index > 0;
  const hasNext = index < files.length - 1;

  // Full-res fetching (current file + neighbor prefetch) waits for navigation
  // to settle — see SETTLE_DELAY_MS. Everything else (thumbnail, name, counter,
  // filmstrip) still tracks `index` directly so the UI never feels delayed.
  useEffect(() => {
    const timer = setTimeout(() => setSettledIndex(index), SETTLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [index]);
  // Infinite scroll for the preview: the grid/list behind the modal isn't
  // scrolling, so its own load-more trigger never fires. New pages append to
  // `files`, so `index` stays valid.
  useEffect(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;
    if (index >= files.length - 1 - LOAD_MORE_THRESHOLD) onLoadMore();
  }, [index, files.length, hasMore, isLoadingMore, onLoadMore]);

  const settledFile = files[settledIndex] ?? file;
  const isSettled = settledFile.id === file.id;

  const goNext = useCallback(() => {
    if (hasNext) { setDirection(1); setIndex((i) => i + 1); }
  }, [hasNext]);
  const goPrev = useCallback(() => {
    if (hasPrev) { setDirection(-1); setIndex((i) => i - 1); }
  }, [hasPrev]);
  const goTo = useCallback((target: number) => {
    if (target < 0 || target >= files.length || target === index) return;
    setDirection(target > index ? 1 : -1);
    setIndex(target);
  }, [files.length, index]);

  // Fetches for the settled file, not the raw one — so scrubbing through
  // several files within SETTLE_DELAY_MS never starts (let alone finishes) a
  // fetch for a file the user has already moved past. Both hooks share the
  // global React Query cache — navigating back to a file that was already
  // viewed never issues a second network request.
  const { data: downloadData, isLoading: urlLoading } = useDownloadUrl(
    settledFile.id,
    explorerUserId,
    trash
  );
  const url = isSettled ? (downloadData?.url ?? null) : null;

  // The list/search endpoints already presign a thumbnail for every media file;
  // the hook is only a fallback for sources that pass in un-enriched items.
  const isMedia = file.mimeType.startsWith("image/") || file.mimeType.startsWith("video/");
  const needsThumbnailFetch = isMedia && file.thumbnailUrl === undefined;
  const fetchedThumbnailUrl = useThumbnailUrl(file.id, needsThumbnailFetch, explorerUserId);
  const thumbnailUrl = file.thumbnailUrl ?? fetchedThumbnailUrl;

  // Prefetch the settled file's neighbors, one batched request for both sides
  // instead of one request each. Keying off settledIndex (not the raw index)
  // means scrubbing through many files never fires a batch per frame — only
  // once navigation actually settles. Neighbors already fresh in cache (e.g.
  // the file you just came from) are skipped. If the user moves on again
  // before this resolves, the AbortController cancels it — the effect cleanup
  // runs on every settledIndex change, so only the latest request ever
  // completes. For images we also force the browser to fetch+decode the
  // actual bytes, so the swipe itself feels instant afterwards.
  useEffect(() => {
    const neighbors = [files[settledIndex - 1], files[settledIndex + 1]].filter(
      (f): f is FileItem =>
        f !== undefined && qc.getQueryData(downloadUrlQueryKey(f.id, explorerUserId, trash)) === undefined
    );
    if (neighbors.length === 0) return;

    const controller = new AbortController();
    const fetchBatch = async () => {
      const ids = neighbors.map((f) => f.id);
      const { urls } = trash
        ? await getTrashDownloadUrls(ids, controller.signal)
        : explorerUserId
          ? await getAdminExplorerDownloadUrls(explorerUserId, ids, controller.signal)
          : await getDownloadUrls(ids, controller.signal);

      for (const neighbor of neighbors) {
        const url = urls[neighbor.id];
        if (!url) continue;
        qc.setQueryData(downloadUrlQueryKey(neighbor.id, explorerUserId, trash), { url });
        if (neighbor.mimeType.startsWith("image/")) new Image().src = url;
      }
    };
    void fetchBatch().catch((err) => {
      if (err instanceof DOMException && err.name === "AbortError") return;
      console.error("Failed to prefetch neighbor download URLs", err);
    });

    return () => controller.abort();
  }, [settledIndex, files, explorerUserId, trash, qc]);

  // Cmd/Ctrl+C copies the current image to the clipboard.
  const copyCurrentImage = useCallback(() => {
    if (!file.mimeType.startsWith("image/")) return;
    if (!url) {
      toast.error("Image is still loading");
      return;
    }
    copyImageToClipboard(url).then(
      () => toast.success("Copied to clipboard"),
      (err) => {
        console.error("Failed to copy image", err);
        toast.error("Couldn't copy image");
      }
    );
  }, [file.mimeType, url]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "c") {
        // Leave normal copy alone when the user has selected text (e.g. in the info panel).
        if (window.getSelection()?.toString()) return;
        e.preventDefault();
        copyCurrentImage();
        return;
      }
      if (e.key === "Escape") {
        // Escape closes the info panel first, then the whole preview.
        if (showInfo) setShowInfo(false);
        else onClose();
      }
      if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); goNext(); }
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); goPrev(); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose, goNext, goPrev, showInfo, copyCurrentImage]);

  const iconType = getMimeIcon(file.mimeType, file.extension);

  const renderPreview = () => {
    // Image/video already have a thumbnail to show immediately, so they never
    // wait on the spinner below — only types with no placeholder do.
    if (iconType === "image") return <ImagePreview url={url} name={file.name} thumbnailUrl={thumbnailUrl} />;
    if (iconType === "video") return <VideoPreview url={url} poster={thumbnailUrl} />;

    // Not settled yet means the debounce timer hasn't caught up to this file's
    // fetch — treat it the same as still loading rather than a hard failure.
    if (!isSettled || urlLoading) return (
      <div className="flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-600 border-t-indigo-400" />
      </div>
    );
    if (!url) return <div className="text-gray-500 text-sm">Failed to load preview</div>;
    switch (iconType) {
      case "audio": return <AudioPreview url={url} name={file.name} mimeType={file.mimeType} />;
      case "code":  return <CodePreview url={url} extension={file.extension} />;
      case "pdf":   return <PdfPreview url={url} />;
      default:      return <GenericPreview url={url} name={file.name} />;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex flex-shrink-0 items-center gap-3 px-4 py-3 border-b border-white/10">
        <button
          onClick={(e) => { e.stopPropagation(); setShowInfo((v) => !v); }}
          className={`flex-shrink-0 rounded-full p-1 transition-colors ${
            showInfo ? "bg-white/10 text-gray-100" : "text-gray-400 hover:bg-white/10 hover:text-gray-100"
          }`}
          title="File details"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
        </button>
        <p className="flex-1 truncate text-sm font-medium text-gray-200">{file.name}</p>
        {isLoadingMore && (
          <span className="text-xs text-gray-500 flex-shrink-0">Loading more…</span>
        )}
        {files.length > 1 && (
          <span className="text-xs text-gray-500 flex-shrink-0">{index + 1} / {files.length}</span>
        )}
        <button
          onClick={onClose}
          className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-white/10 hover:text-gray-100 transition-colors"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>
      </div>

      {showInfo && <PreviewFileInfo file={file} onClose={() => setShowInfo(false)} />}

      {/* ── Content with side nav arrows ─────────────────────────────────── */}
      <div className="relative flex flex-1 items-center overflow-hidden">
        <button
          onClick={goPrev}
          disabled={!hasPrev}
          className={`absolute left-2 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white backdrop-blur-sm transition-all sm:left-3 sm:h-10 sm:w-10 ${
            hasPrev ? "opacity-100 hover:bg-white/20 cursor-pointer" : "opacity-0 pointer-events-none"
          }`}
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
          </svg>
        </button>

        <div className="flex h-full flex-1 items-center justify-center w-full max-w-5xl mx-auto px-10 py-4 overflow-hidden sm:px-16">
          <div
            key={file.id}
            className={`flex h-full w-full items-center justify-center ${
              direction === 1 ? "animate-slide-in-right" : "animate-slide-in-left"
            }`}
          >
            {renderPreview()}
          </div>
        </div>

        <button
          onClick={goNext}
          disabled={!hasNext}
          className={`absolute right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white backdrop-blur-sm transition-all sm:right-3 sm:h-10 sm:w-10 ${
            hasNext ? "opacity-100 hover:bg-white/20 cursor-pointer" : "opacity-0 pointer-events-none"
          }`}
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      <PreviewFilmstrip files={files} index={index} onSelect={goTo} />
    </div>
  );
}
